/* eslint-disable import/no-extraneous-dependencies */
import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import Model, { Providers } from '@tripian/model';
import classes from './KiwiBookingModal.scss';
import { getCurrencySymbol } from '../../currencySymbol';

interface IKiwiBookingModal {
  hotelName: string;
  roomTitle: string;
  rate: Providers.Kiwi.KiwiRate;
  currency: string;
  checkIn: string;
  checkOut: string;
  nightCount: number;
  adults: number;
  children: number;
  propertyCode: string;
  roomCode: string;
  generateExternalId: () => string;
  onClose: () => void;
  onCreateReservation: (req: Providers.Kiwi.KiwiCreateReservationRequest) => Promise<Providers.Kiwi.KiwiCreateReservationResponse>;
  onReservationFailed?: (errorMessage: string) => void;
  t: (value: Model.TranslationKey) => string;
}

type LeadGuestForm = {
  title: string;
  first_name: string;
  last_name: string;
  email: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  country: string;
  zip_postal: string;
  phone_country_code: string;
  phone_area_code: string;
  phone: string;
};


type PaymentForm = {
  cardholder_name: string;
  card_number: string;
  card_type: string;
  card_security: string;
  expiry_month: string;
  expiry_year: string;
  message_to_hotel: string;
};

type FormErrors = Partial<Record<string, string>>;

const CARD_TYPES = [
  { value: 'VI', label: 'Visa' },
  { value: 'MC', label: 'Mastercard' },
  { value: 'AX', label: 'American Express' },
];

const TITLES = ['Mr', 'Mrs', 'Ms', 'Dr'];
const MONTHS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 15 }, (_, i) => String(currentYear + i));

const fmtDate = (dateStr: string) => {
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return dateStr; }
};

const fmtPrice = (val: string | number | undefined, currency: string) => {
  const n = parseFloat(String(val ?? 0)) || 0;
  return `${getCurrencySymbol(currency)}${new Intl.NumberFormat('en-US').format(Math.round(n))}`;
};

const emptyLead: LeadGuestForm = {
  title: 'Mr', first_name: '', last_name: '', email: '',
  address_line1: '', address_line2: '', city: '', state: '',
  country: '', zip_postal: '', phone_country_code: '', phone_area_code: '', phone: '',
};


const emptyPayment: PaymentForm = {
  cardholder_name: '', card_number: '', card_type: 'VI',
  card_security: '', expiry_month: '01', expiry_year: String(currentYear), message_to_hotel: '',
};

const validateLead = (f: LeadGuestForm, t: (key: Model.TranslationKey) => string): FormErrors => {
  const e: FormErrors = {};
  if (!f.first_name.trim()) e.lead_first_name = t('kiwi.booking.validation.required');
  if (!f.last_name.trim()) e.lead_last_name = t('kiwi.booking.validation.required');
  if (!f.email.trim()) e.lead_email = t('kiwi.booking.validation.required');
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.lead_email = t('kiwi.booking.validation.invalidEmail');
  if (!f.address_line1.trim()) e.lead_address_line1 = t('kiwi.booking.validation.required');
  if (!f.city.trim()) e.lead_city = t('kiwi.booking.validation.required');
  if (!f.country.trim()) e.lead_country = t('kiwi.booking.validation.required');
  if (!f.zip_postal.trim()) e.lead_zip_postal = t('kiwi.booking.validation.required');
  if (!f.phone_country_code.trim()) e.lead_phone_country_code = t('kiwi.booking.validation.required');
  if (!f.phone.trim()) e.lead_phone = t('kiwi.booking.validation.required');
  return e;
};


const validateChildren = (ages: string[], t: (key: Model.TranslationKey) => string): FormErrors => {
  const e: FormErrors = {};
  ages.forEach((a, i) => {
    const n = parseInt(a, 10);
    if (a === '' || Number.isNaN(n)) e[`child_${i}`] = t('kiwi.booking.validation.required');
    else if (n < 0 || n > 17) e[`child_${i}`] = t('kiwi.booking.validation.childAge');
  });
  return e;
};

const validatePayment = (f: PaymentForm, t: (key: Model.TranslationKey) => string): FormErrors => {
  const e: FormErrors = {};
  if (!f.cardholder_name.trim()) e.cardholder_name = t('kiwi.booking.validation.required');
  if (!f.card_number.trim()) e.card_number = t('kiwi.booking.validation.required');
  else if (!/^\d{13,19}$/.test(f.card_number.replace(/\s/g, ''))) e.card_number = t('kiwi.booking.validation.invalidCard');
  if (!f.card_security.trim()) e.card_security = t('kiwi.booking.validation.required');
  else if (!/^\d{3,4}$/.test(f.card_security)) e.card_security = t('kiwi.booking.validation.invalidCvv');
  return e;
};

const Field: React.FC<{
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}> = ({ label, required, error, children: fieldChildren }) => (
  <div className={classes.field}>
    <label className={classes.label}>{label}{required && <span className={classes.required}>*</span>}</label>
    {fieldChildren}
    {error && <span className={classes.fieldError}>{error}</span>}
  </div>
);

const KiwiBookingModal: React.FC<IKiwiBookingModal> = ({
  hotelName,
  roomTitle,
  rate,
  currency,
  checkIn,
  checkOut,
  nightCount,
  adults,
  children,
  propertyCode,
  roomCode,
  generateExternalId,
  onClose,
  onCreateReservation,
  onReservationFailed,
  t,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [lead, setLead] = useState<LeadGuestForm>(emptyLead);
  const [childrenAges, setChildrenAges] = useState<string[]>(
    () => Array.from({ length: children }, () => ''),
  );
  const [payment, setPayment] = useState<PaymentForm>(emptyPayment);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [confirmationId, setConfirmationId] = useState('');

  const total = rate.price?.inclusiveTotal;
  const hasChildren = children > 0;

  const guestCountLabel = useMemo(() => {
    const parts = [];
    if (adults > 0) parts.push(`${adults} ${adults === 1 ? t('kiwi.searchBar.adult') : t('kiwi.common.adults')}`);
    if (children > 0) parts.push(`${children} ${children === 1 ? t('kiwi.searchBar.child') : t('kiwi.common.children')}`);
    return parts.join(', ');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adults, children]);

  const handleLeadChange = (k: keyof LeadGuestForm, v: string) => {
    setLead((prev) => ({ ...prev, [k]: v }));
    if (errors[`lead_${k}`]) setErrors((prev) => { const n = { ...prev }; delete n[`lead_${k}`]; return n; });
  };


  const handleChildAgeChange = (i: number, v: string) => {
    setChildrenAges((prev) => prev.map((a, idx) => idx === i ? v : a));
    if (errors[`child_${i}`]) setErrors((prev) => { const n = { ...prev }; delete n[`child_${i}`]; return n; });
  };

  const handlePaymentChange = (k: keyof PaymentForm, v: string) => {
    setPayment((prev) => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors((prev) => { const n = { ...prev }; delete n[k]; return n; });
  };

  const goNext = () => {
    if (step === 1) {
      const e = {
        ...validateLead(lead, t),
        ...(hasChildren ? validateChildren(childrenAges, t) : {}),
      };
      if (Object.keys(e).length > 0) { setErrors(e); return; }
      setErrors({});
      setStep(2);
    } else if (step === 2) {
      const e = validatePayment(payment, t);
      if (Object.keys(e).length > 0) { setErrors(e); return; }
      setErrors({});
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setApiError('');
    const externalId = generateExternalId();
    try {
      const leadContactInfo: Providers.Kiwi.KiwiReservationGuestContactInfo = {
        email: lead.email,
        address_line1: lead.address_line1,
        ...(lead.address_line2 ? { address_line2: lead.address_line2 } : {}),
        city: lead.city,
        state: lead.state,
        country: lead.country,
        zip_postal: lead.zip_postal,
        phone_country_code: lead.phone_country_code,
        phone_area_code: lead.phone_area_code,
        phone: lead.phone,
      };

      const allGuests: Providers.Kiwi.KiwiReservationGuest[] = [
        { title: lead.title, first_name: lead.first_name, last_name: lead.last_name, contact_info: leadContactInfo },
      ];

      const parsedChildrenAges = childrenAges.map((a) => parseInt(a, 10)).filter((n) => !Number.isNaN(n));

      const req: Providers.Kiwi.KiwiCreateReservationRequest = {
        property_code: propertyCode,
        room_code: roomCode,
        rate_code: rate.code,
        in_date: checkIn,
        out_date: checkOut,
        adults,
        ...(children > 0 ? { children } : {}),
        ...(parsedChildrenAges.length > 0 ? { children_ages: parsedChildrenAges } : {}),
        rooms: 1,
        external_id: externalId,
        guests: allGuests,
        payment_info: {
          cardholder_name: payment.cardholder_name,
          card_number: payment.card_number.replace(/\s/g, ''),
          card_type: payment.card_type,
          card_security: payment.card_security,
          expiry_month: payment.expiry_month,
          expiry_year: payment.expiry_year,
        },
        ...(payment.message_to_hotel ? { message_to_hotel: payment.message_to_hotel } : {}),
      };

      const res = await onCreateReservation(req);
      if (res.data) {
        setConfirmationId(res.data.confirmationId || externalId);
        setStep(3);
      } else {
        const msg = t('kiwi.booking.error.failed');
        setApiError(msg);
        if (onReservationFailed) onReservationFailed(msg);
      }
    } catch (err) {
      const msg = t('kiwi.booking.error.generic');
      setApiError(msg);
      if (onReservationFailed) onReservationFailed(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={classes.overlay} onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={classes.panel} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className={classes.header}>
          <div className={classes.headerInfo}>
            <p className={classes.headerTitle}>{hotelName}</p>
            <p className={classes.headerSub}>{roomTitle}</p>
          </div>
          <button type="button" className={classes.closeBtn} onClick={onClose} aria-label={t('kiwi.common.close')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Summary bar */}
        {step !== 3 && (
          <div className={classes.summaryBar}>
            <div className={classes.summaryItem}>
              <span className={classes.summaryLabel}>{t('kiwi.common.checkIn')}</span>
              <span className={classes.summaryValue}>{fmtDate(checkIn)}</span>
            </div>
            <div className={classes.summaryItem}>
              <span className={classes.summaryLabel}>{t('kiwi.common.checkOut')}</span>
              <span className={classes.summaryValue}>{fmtDate(checkOut)}</span>
            </div>
            <div className={classes.summaryItem}>
              <span className={classes.summaryLabel}>{t('kiwi.common.nights')}</span>
              <span className={classes.summaryValue}>{nightCount}</span>
            </div>
            <div className={classes.summaryItem}>
              <span className={classes.summaryLabel}>{t('kiwi.common.guests')}</span>
              <span className={classes.summaryValue}>{guestCountLabel}</span>
            </div>
            <div className={classes.summaryItem}>
              <span className={classes.summaryLabel}>{t('kiwi.common.total')}</span>
              <span className={classes.summaryValue}>{fmtPrice(total, currency)} {currency}</span>
            </div>
          </div>
        )}

        {/* Step tabs */}
        {step !== 3 && (
          <div className={classes.steps}>
            <div className={`${classes.step} ${step === 1 ? classes.stepActive : classes.stepDone}`}>{t('kiwi.booking.step1')}</div>
            <div className={`${classes.step} ${step === 2 ? classes.stepActive : step > 2 ? classes.stepDone : ''}`}>{t('kiwi.booking.step2')}</div>
          </div>
        )}

        {/* Body */}
        <div className={classes.body}>
          {apiError && <div className={classes.errorBanner}>{apiError}</div>}

          {/* ── Step 1: Guest Info ── */}
          {step === 1 && (
            <>
              {/* Lead guest */}
              <p className={classes.sectionTitle}>{t('kiwi.booking.guestInformation')}</p>

              <div className={classes.row}>
                <Field label={t('kiwi.booking.title')} required>
                  <select className={classes.select} value={lead.title} onChange={(e) => handleLeadChange('title', e.target.value)}>
                    {TITLES.map((tt) => <option key={tt} value={tt}>{tt}</option>)}
                  </select>
                </Field>
              </div>
              <div className={classes.row}>
                <Field label={t('kiwi.booking.firstName')} required error={errors.lead_first_name}>
                  <input className={`${classes.input} ${errors.lead_first_name ? classes.error : ''}`} value={lead.first_name} onChange={(e) => handleLeadChange('first_name', e.target.value)} placeholder="John" />
                </Field>
                <Field label={t('kiwi.booking.lastName')} required error={errors.lead_last_name}>
                  <input className={`${classes.input} ${errors.lead_last_name ? classes.error : ''}`} value={lead.last_name} onChange={(e) => handleLeadChange('last_name', e.target.value)} placeholder="Doe" />
                </Field>
              </div>
              <Field label={t('kiwi.booking.emailAddress')} required error={errors.lead_email}>
                <input type="email" className={`${classes.input} ${errors.lead_email ? classes.error : ''}`} value={lead.email} onChange={(e) => handleLeadChange('email', e.target.value)} placeholder="john@example.com" />
              </Field>

              <hr className={classes.sectionDivider} />
              <p className={classes.sectionTitle}>{t('kiwi.booking.address')}</p>

              <Field label={t('kiwi.booking.addressLine1')} required error={errors.lead_address_line1}>
                <input className={`${classes.input} ${errors.lead_address_line1 ? classes.error : ''}`} value={lead.address_line1} onChange={(e) => handleLeadChange('address_line1', e.target.value)} placeholder="123 Main St" />
              </Field>
              <Field label={t('kiwi.booking.addressLine2')}>
                <input className={classes.input} value={lead.address_line2} onChange={(e) => handleLeadChange('address_line2', e.target.value)} placeholder="Apt, suite, etc. (optional)" />
              </Field>
              <div className={classes.row}>
                <Field label={t('kiwi.booking.city')} required error={errors.lead_city}>
                  <input className={`${classes.input} ${errors.lead_city ? classes.error : ''}`} value={lead.city} onChange={(e) => handleLeadChange('city', e.target.value)} placeholder="New York" />
                </Field>
                <Field label={t('kiwi.booking.stateProvince')}>
                  <input className={classes.input} value={lead.state} onChange={(e) => handleLeadChange('state', e.target.value)} placeholder="NY" />
                </Field>
              </div>
              <div className={classes.row}>
                <Field label={t('kiwi.booking.country')} required error={errors.lead_country}>
                  <input className={`${classes.input} ${errors.lead_country ? classes.error : ''}`} value={lead.country} onChange={(e) => handleLeadChange('country', e.target.value)} placeholder="US" />
                </Field>
                <Field label={t('kiwi.booking.zipPostalCode')} required error={errors.lead_zip_postal}>
                  <input className={`${classes.input} ${errors.lead_zip_postal ? classes.error : ''}`} value={lead.zip_postal} onChange={(e) => handleLeadChange('zip_postal', e.target.value)} placeholder="10001" />
                </Field>
              </div>

              <hr className={classes.sectionDivider} />
              <p className={classes.sectionTitle}>{t('kiwi.booking.phoneNumberSection')}</p>
              <div className={classes.row3}>
                <Field label={t('kiwi.booking.countryCode')} required error={errors.lead_phone_country_code}>
                  <input className={`${classes.input} ${errors.lead_phone_country_code ? classes.error : ''}`} value={lead.phone_country_code} onChange={(e) => handleLeadChange('phone_country_code', e.target.value)} placeholder="+1" />
                </Field>
                <Field label={t('kiwi.booking.areaCode')}>
                  <input className={classes.input} value={lead.phone_area_code} onChange={(e) => handleLeadChange('phone_area_code', e.target.value)} placeholder="212" />
                </Field>
                <Field label={t('kiwi.booking.phone')} required error={errors.lead_phone}>
                  <input className={`${classes.input} ${errors.lead_phone ? classes.error : ''}`} value={lead.phone} onChange={(e) => handleLeadChange('phone', e.target.value)} placeholder="5551234" />
                </Field>
              </div>

              {/* Children ages */}
              {hasChildren && (
                <>
                  <hr className={classes.sectionDivider} />
                  <p className={classes.sectionTitle}>{t('kiwi.booking.childrenAges')}</p>
                  <div className={classes.row}>
                    {childrenAges.map((age, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Field key={i} label={`${t('kiwi.booking.childAge')} ${i + 1}`} required error={errors[`child_${i}`]}>
                        <input
                          className={`${classes.input} ${errors[`child_${i}`] ? classes.error : ''}`}
                          value={age}
                          onChange={(e) => handleChildAgeChange(i, e.target.value.replace(/\D/g, '').slice(0, 2))}
                          placeholder="0–17"
                          inputMode="numeric"
                        />
                      </Field>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* ── Step 2: Payment ── */}
          {step === 2 && (
            <>
              <p className={classes.sectionTitle}>{t('kiwi.booking.paymentDetails')}</p>
              <Field label={t('kiwi.booking.cardholderName')} required error={errors.cardholder_name}>
                <input className={`${classes.input} ${errors.cardholder_name ? classes.error : ''}`} value={payment.cardholder_name} onChange={(e) => handlePaymentChange('cardholder_name', e.target.value)} placeholder="John Doe" />
              </Field>
              <Field label={t('kiwi.booking.cardType')} required>
                <select className={classes.select} value={payment.card_type} onChange={(e) => handlePaymentChange('card_type', e.target.value)}>
                  {CARD_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </Field>
              <Field label={t('kiwi.booking.cardNumber')} required error={errors.card_number}>
                <div className={classes.cardInputWrap}>
                  <input
                    className={`${classes.input} ${classes.cardInput} ${errors.card_number ? classes.error : ''}`}
                    value={payment.card_number}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, '').slice(0, 19);
                      const formatted = raw.replace(/(.{4})/g, '$1 ').trim();
                      handlePaymentChange('card_number', formatted);
                    }}
                    placeholder="0000 0000 0000 0000"
                    inputMode="numeric"
                  />
                </div>
              </Field>
              <div className={classes.row}>
                <Field label={t('kiwi.booking.cvv')} required error={errors.card_security}>
                  <input
                    className={`${classes.input} ${errors.card_security ? classes.error : ''}`}
                    value={payment.card_security}
                    onChange={(e) => handlePaymentChange('card_security', e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="123"
                    inputMode="numeric"
                  />
                </Field>
              </div>
              <div className={classes.row}>
                <Field label={t('kiwi.booking.expiryMonth')} required>
                  <select className={classes.select} value={payment.expiry_month} onChange={(e) => handlePaymentChange('expiry_month', e.target.value)}>
                    {MONTHS.map((mo) => <option key={mo} value={mo}>{mo}</option>)}
                  </select>
                </Field>
                <Field label={t('kiwi.booking.expiryYear')} required>
                  <select className={classes.select} value={payment.expiry_year} onChange={(e) => handlePaymentChange('expiry_year', e.target.value)}>
                    {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </Field>
              </div>

              <hr className={classes.sectionDivider} />
              <Field label={t('kiwi.booking.messageToHotel')}>
                <textarea
                  className={classes.textarea}
                  value={payment.message_to_hotel}
                  onChange={(e) => handlePaymentChange('message_to_hotel', e.target.value)}
                  placeholder="Any special requests or notes for the hotel..."
                />
              </Field>
            </>
          )}

          {/* ── Step 3: Success ── */}
          {step === 3 && (
            <div className={classes.success}>
              <div className={classes.successIcon}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h2 className={classes.successTitle}>{t('kiwi.booking.confirmed.title')}</h2>
              <p className={classes.successSub}>{t('kiwi.booking.confirmed.subtitle')}</p>
              <div className={classes.confirmCard}>
                <div className={classes.confirmRow}>
                  <span className={classes.confirmLabel}>{t('kiwi.booking.confirmed.confirmationId')}</span>
                  <span className={classes.confirmId}>{confirmationId}</span>
                </div>
                <div className={classes.confirmRow}>
                  <span className={classes.confirmLabel}>{t('kiwi.booking.confirmed.hotel')}</span>
                  <span className={classes.confirmValue}>{hotelName}</span>
                </div>
                <div className={classes.confirmRow}>
                  <span className={classes.confirmLabel}>{t('kiwi.booking.confirmed.room')}</span>
                  <span className={classes.confirmValue}>{roomTitle}</span>
                </div>
                <div className={classes.confirmRow}>
                  <span className={classes.confirmLabel}>{t('kiwi.common.checkIn')}</span>
                  <span className={classes.confirmValue}>{fmtDate(checkIn)}</span>
                </div>
                <div className={classes.confirmRow}>
                  <span className={classes.confirmLabel}>{t('kiwi.common.checkOut')}</span>
                  <span className={classes.confirmValue}>{fmtDate(checkOut)}</span>
                </div>
                <div className={classes.confirmRow}>
                  <span className={classes.confirmLabel}>{t('kiwi.common.guests')}</span>
                  <span className={classes.confirmValue}>{guestCountLabel}</span>
                </div>
                <div className={classes.confirmRow}>
                  <span className={classes.confirmLabel}>{t('kiwi.common.total')}</span>
                  <span className={classes.confirmValue}>{fmtPrice(total, currency)} {currency}</span>
                </div>
                <div className={classes.confirmRow}>
                  <span className={classes.confirmLabel}>Lead Guest</span>
                  <span className={classes.confirmValue}>{lead.first_name} {lead.last_name}</span>
                </div>
              </div>
              <button type="button" className={classes.doneBtn} onClick={onClose}>{t('kiwi.booking.confirmed.done')}</button>
            </div>
          )}
        </div>

        {/* Footer */}
        {step !== 3 && (
          <div className={classes.footer}>
            {step === 2 ? (
              <button type="button" className={classes.backBtn} onClick={() => { setErrors({}); setApiError(''); setStep(1); }}>{t('kiwi.common.back')}</button>
            ) : (
              <span />
            )}
            <button type="button" className={classes.nextBtn} onClick={goNext} disabled={submitting}>
              {submitting && <span className={classes.spinner} />}
              {step === 1 ? t('kiwi.booking.continueToPayment') : submitting ? t('kiwi.booking.processing') : t('kiwi.booking.confirmBooking')}
            </button>
          </div>
        )}

      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default KiwiBookingModal;
